/**
 * @fileoverview Mobile tenant runtime configuration schema for the
 *               complex-inspections contract bundle (document 15).
 * @module import-core/contracts/complex-inspections/mobileRuntimeSchemas
 *
 * Responsibilities:
 * - Define the department-wide `mobileTenantRuntime` singleton document that
 *   the mobile API projects as a virtual pull-only sync collection and
 *   mobile-pwa stores in the `mobile_tenant_runtime` Dexie table.
 *
 * Dependencies:
 * - `zod` v3; `./sharedSchemas` primitives.
 *
 * Architecture:
 * - Persisted-boundary schema for a server-authoritative projection. The
 *   document is assembled by the mobile API virtual pull from system-DB
 *   tenant/module/flag sources; it is never written by a client and never
 *   appears in a push allowlist.
 *
 * Notes:
 * - `_id` is the fixed singleton key `'current'` (not an ObjectId) so Dexie
 *   holds exactly one record per tenant database. Tenant identity is embedded
 *   (`tenantId`) so client reset/full reconciliation can detect a stale
 *   singleton from a prior tenant.
 */

import { z } from 'zod';
import { isoDateLikeSchema, entityCategorySchema } from './sharedSchemas';

export const MOBILE_TENANT_RUNTIME_SINGLETON_ID = 'current' as const;
export const COMPLEX_INSPECTIONS_FLAG_KEY = 'complex-inspections-v1' as const;

/**
 * @contract runtimeModuleEntrySchema
 * Purpose: One resolved product-module entry projected to mobile clients.
 * Rules: sorted by code; `isEnabled` is the effective value after
 *   tenantModules override > embedded tenants.modules > enabledByDefault.
 */
export const runtimeModuleEntrySchema = z
  .object({
    code: z.string(),
    name: z.string(),
    isEnabled: z.boolean(),
  })
  .strict();

/**
 * @contract runtimeCapabilityMatrixSchema
 * Purpose: Derived offline capability matrix — which entity categories and
 *   workflows the runtime may exercise. De-duplicated and sorted.
 */
export const runtimeCapabilityMatrixSchema = z
  .object({
    entityCategories: z.array(entityCategorySchema),
    workflows: z.array(z.string()),
  })
  .strict();

/**
 * @contract rolloutFlagsSchema
 * Purpose: Old-client compatibility block retained in runtime schema v1.
 * Rules: `complexInspectionsV1` is deprecated and always true after GA; no feature-flag row controls it.
 */
export const rolloutFlagsSchema = z
  .object({
    complexInspectionsV1: z.boolean(),
  })
  .strict();

/**
 * @contract mobileTenantRuntimeConfigSchema
 * Purpose: Complete pull-only `mobileTenantRuntime` sync document. Contains
 *   effective modules, derived capabilities, rollout flags, and allowlisted
 *   tenant fiscal-year/map defaults.
 * Rules: `sourceUpdatedAt` is the deterministic max source timestamp used for
 *   checkpoint/delta semantics; `updatedAt` equals it so replication sees a
 *   stable document until sources actually change.
 */
export const mobileTenantRuntimeConfigSchema = z
  .object({
    _id: z.literal(MOBILE_TENANT_RUNTIME_SINGLETON_ID),
    schemaVersion: z.literal(1),
    // Any non-empty tenant identifier — not ObjectId-only (document 15).
    tenantId: z.string().min(1),
    tenantSlug: z.string().min(1),
    effectiveModules: z.array(runtimeModuleEntrySchema),
    capabilities: runtimeCapabilityMatrixSchema,
    rolloutFlags: rolloutFlagsSchema,
    fiscalYear: z
      .object({
        startMonth: z.number().int().min(1).max(12),
        startDay: z.number().int().min(1).max(31),
        labelMode: z.string().optional(),
      })
      .strict()
      .optional(),
    mapDefaults: z
      .object({
        // [longitude, latitude] — mapped deterministically by the mobile API
        // from the tenant's stored center fields (document 15).
        center: z.tuple([z.number(), z.number()]).optional(),
        zoom: z.number().optional(),
        styleRef: z.string().optional(),
      })
      .strict()
      .optional(),
    sourceUpdatedAt: isoDateLikeSchema,
    updatedAt: isoDateLikeSchema,
    _deleted: z.boolean().optional(),
  })
  .strict();

export type MobileTenantRuntimeConfig = z.infer<typeof mobileTenantRuntimeConfigSchema>;
export type RuntimeModuleEntry = z.infer<typeof runtimeModuleEntrySchema>;
export type RuntimeCapabilityMatrix = z.infer<typeof runtimeCapabilityMatrixSchema>;
export type RolloutFlags = z.infer<typeof rolloutFlagsSchema>;
